 
import React from "react";
import {
  AccountCircle,
  AdminPanelSettings,
  StickyNote2,
} from "@mui/icons-material";
import { TabPanel } from "../../../../../components/Tabs/TabPanel";
import { PageLoader } from "../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../constants/enums";
import { useCustomerPolicyDetails, useQueryParms,
} from "../../../../../hooks";
import { TabsList } from "../../../../../components/Tabs/TabsList";
import { useNavigate, useOutletContext } from "react-router-dom";
import { CommAutomobileScheduleAList } from "./CommAutoSchedule/CommAutoScheduleA/CommAutoScheduleAList";
import { CommAutomobileScheduleBList } from "./CommAutoSchedule/CommAutoScheduleB/CommAutoScheduleBList";
import { CommAutomobileScheduleCList } from "./CommAutoSchedule/CommAutoScheduleC/CommAutoScheduleCList";
import { CommAutomobileScheduleDList } from "./CommAutoSchedule/CommAutoScheduleD/CommAutoScheduleDList";
import { PageContainer } from "../../../../../components/container/PageContainer";
import { useCommAutoScope } from "../../../../leads/ManageLead/LeadCommAutoScopeContext";

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * The four Coverage sub-tabs. Kept as a single ordered source of truth so the
 * sub-tab set is identical in both the customer-policy and lead scopes
 * (Requirements 3.4, 9.1 — no sub-tab added/removed/renamed/reordered).
 */
const COVERAGE_TABS = [
  { label: "IRCA/Scheduled Fleet", icon: <AccountCircle /> },
  { label: "Blanket Fleet", icon: <AdminPanelSettings /> },
  { label: "Garage (Ontario)", icon: <StickyNote2 /> },
  { label: "Garage (Alberta)", icon: <StickyNote2 /> },
];

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Optional lead-scope props. All are additive and optional so the
 * customer-policy route rendering (`useOutletContext` + `?tab=` + `navigate`)
 * is unchanged when none are supplied (Requirements 16.1, 16.2).
 *
 * - `selectedTab` / `onTabChange`: when BOTH are provided the inner `TabsList`
 *   is controlled by local state (lead scope) and `navigate` is never called.
 *   When absent the root keeps reading `?tab=` and calling `navigate(...)`
 *   exactly as before (Requirement 4.4).
 * - `onCreate` / `onEdit`: optional per-sub-tab in-panel create/edit callbacks
 *   passed down to the lists to replace route navigation in lead scope
 *   (Requirements 4.1, 9.2).
 */
interface ICommAutoVehicleCoverageScheduleRootProps {
  selectedTab?: number;
  onTabChange?: (newValue: number) => void;
  onCreate?: (subTab: number) => void;
  onEdit?: (
    subTab: number,
    incrementalNumber: number | null,
    vehicleNumber?: number
  ) => void;
}

/**
 * Safe wrapper around `useOutletContext` that returns `undefined` instead of
 * throwing when the root is not rendered under a React Router `<Outlet>` (which
 * is the case inside the Lead Comm Auto panel, since that panel is not a
 * route). In the customer-policy context the outlet tuple is
 * `[customerId, customerPolicyId]`.
 */
const useOutletContextSafe = (): unknown[] | undefined => {
  try {
    return useOutletContext<unknown[]>();
  } catch {
    return undefined;
  }
};

export const CommAutoVehicleCoverageScheduleRoot: React.FC<
  ICommAutoVehicleCoverageScheduleRootProps
> = (props) => {
  const { selectedTab: controlledTab, onTabChange, onCreate, onEdit } = props;

  // Keep reading the router outlet in the customer-policy context; in lead
  // scope this returns `undefined` (the panel is not a route) and the resolved
  // scope comes from the LeadCommAutoScopeProvider instead.
  const outlet = useOutletContextSafe();
  const outletCustomerId = outlet?.[0] as number | undefined;
  const outletCustomerPolicyId = outlet?.[1] as number | undefined;

  // Lead scope wins; else the route fallback verbatim (Requirements 5.2, 5.3).
  const { customerId, customerPolicyId, leadId } = useCommAutoScope({
    customerId: outletCustomerId,
    customerPolicyId: outletCustomerPolicyId,
  });

  const { getParm } = useQueryParms();
  const tabValue = getParm("tab");

  // Controlled selection is used only when the caller supplies both props
  // (lead scope). Otherwise selection is driven by the `?tab=` query param
  // exactly as before (Requirement 16.2).
  const isControlled = controlledTab != null && onTabChange != null;
  const selectedTab = isControlled
    ? controlledTab
    : tabValue
      ? Number(tabValue)
      : 0;

  const { details } = useCustomerPolicyDetails(customerPolicyId as number);
  const navigate = useNavigate();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isControlled) {
      onTabChange(newValue);
      return;
    }
    navigate(
      `/comm-automobile-policy/${customerId}/vehicle-coverage-schedule-tab/${customerPolicyId}?tab=${newValue}`
    );
  };

  // In lead scope the create/edit callbacks (if supplied) replace route
  // navigation; they are curried per sub-tab so each list keeps its own
  // handler signature. Absent the callbacks the props are `undefined` and the
  // lists fall back to their existing `navigate(...)` behavior.
  const makeOnCreate = (subTab: number) =>
    onCreate ? () => onCreate(subTab) : undefined;
  const makeOnEditA = (subTab: number) =>
    onEdit
      ? (n: number | null, vehicleNumber?: number) =>
          onEdit(subTab, n, vehicleNumber)
      : undefined;
  const makeOnEdit = (subTab: number) =>
    onEdit ? (n: number | null) => onEdit(subTab, n) : undefined;

  return (
    <PageContainer title={"Vehicle Coverage Schedule"} description="this is innerpage">
    <PageLoader
      loading={details.loading === LoadState.InProgress}
      error={details.error ? { message: details.error } : null}
    >
      <>
        <TabsList
          sx={{ mb: 1 }}
          varaint="fullWidth"
          selectedTab={selectedTab}
          tabs={COVERAGE_TABS}
          onChange={handleChange}
        />
        <TabPanel value={selectedTab} index={0}>
          <CommAutomobileScheduleAList
            customerId={customerId as number}
            customerPolicyId={customerPolicyId as number}
            leadId={leadId}
            onCreate={makeOnCreate(0)}
            onEdit={makeOnEditA(0)}
          />
        </TabPanel>

        <TabPanel value={selectedTab} index={1}>
          <CommAutomobileScheduleBList
            customerId={customerId as number}
            customerPolicyId={customerPolicyId as number}
            leadId={leadId}
            onCreate={makeOnCreate(1)}
            onEdit={makeOnEdit(1)}
          />
        </TabPanel>

        <TabPanel value={selectedTab} index={2}>
          <CommAutomobileScheduleCList
            customerId={customerId as number}
            customerPolicyId={customerPolicyId as number}
            leadId={leadId}
            onCreate={makeOnCreate(2)}
            onEdit={makeOnEdit(2)}
          />
        </TabPanel>
        <TabPanel value={selectedTab} index={3}>
          <CommAutomobileScheduleDList
            customerId={customerId as number}
            customerPolicyId={customerPolicyId as number}
            leadId={leadId}
            onCreate={makeOnCreate(3)}
            onEdit={makeOnEdit(3)}
          />
        </TabPanel>
      </>
    </PageLoader>
    </PageContainer>
  );
};
